import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { getPushTokenIfGranted, areNotificationsEnabled, getPushPlatform } from '../services/pushService';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage for instant display (no "Ospite" flash)
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_user');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(() => {
    // If we have cached user data, skip the loading state entirely
    return !localStorage.getItem('cached_user');
  });

  // Wrapper to persist user data to localStorage
  const setUserAndCache = (userData) => {
    setUser(userData);
    if (userData) {
      try { localStorage.setItem('cached_user', JSON.stringify(userData)); } catch {}
    } else {
      localStorage.removeItem('cached_user');
    }
  };
  // Includes retry logic and localStorage tracking
  const registerPushToken = async (retryCount = 0) => {
    try {
      if (!areNotificationsEnabled()) return;

      // Force Service Worker update to pick up new CSP-compatible version
      if ('serviceWorker' in navigator && retryCount === 0) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) await reg.update();
        } catch (swErr) {
          console.warn('SW update check failed:', swErr.message);
        }
      }

      const platform = getPushPlatform();
      const isNative = platform === 'ios' || platform === 'android';

      // STRATEGY for boosting push coverage (from 41% → 70%+):
      //
      // On NATIVE, we do NOT call `requestNotificationPermission()` here
      // (which would re-trigger the iOS/Android system prompt on first
      // call, and is a no-op afterwards) — that flow is now owned by the
      // soft-ask modal which has a much higher acceptance rate.
      //
      // Instead, every time the app boots / the user authenticates we:
      //   1. Read the CURRENT OS permission state (no prompt, no side-effects)
      //   2. If it's already `granted` — try a silent `register()` to fetch
      //      the latest device token and POST it to the backend. This
      //      recovers users who said yes months ago but whose token never
      //      reached us (the dominant cause of the gap between OS-granted
      //      and our DB).
      //   3. If it's `denied` / `prompt` — bail out silently. The soft-ask
      //      modal and the re-engagement banner will handle UX.
      //
      // On WEB we keep the legacy flow (single prompt, Firebase Web SDK).
      let fcmToken = null;
      if (isNative) {
        try {
          const { checkNativeNotificationPermission, fetchNativeTokenSilently } =
            await import('../services/nativePush');
          const state = await checkNativeNotificationPermission();
          // Sync OS-permission state with the backend so the re-engagement
          // banner can light up immediately for users who denied/disabled
          // at the OS level outside of our soft-ask flow. Silently.
          if (state === 'denied') {
            axios.post(`${API}/auth/push-softask`, { action: 'system_denied' }).catch(() => {});
            return;
          }
          if (state !== 'granted') return;       // 'prompt' → soft-ask handles it
          fcmToken = await fetchNativeTokenSilently();
        } catch (silentErr) {
          console.warn('Silent native token fetch failed:', silentErr?.message || silentErr);
        }
      } else {
        // Web fallback — the legacy flow handles the first-time prompt
        // through the same gate (only triggers a prompt when explicitly
        // asked by the user via UI button on the profile page).
        fcmToken = await getPushTokenIfGranted();
      }

      if (fcmToken) {
        await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform });
        localStorage.setItem('push_token_registered', 'true');
        console.log(`Push token registered (platform=${platform}, token=${fcmToken.substring(0, 15)}...)`);
      } else {
        console.warn(`Push token is null (platform=${platform}), retry=${retryCount}`);
        localStorage.removeItem('push_token_registered');
        // Retry after delay (max 2 retries)
        if (retryCount < 2) {
          setTimeout(() => registerPushToken(retryCount + 1), 3000 * (retryCount + 1));
        }
      }
    } catch (e) {
      console.warn('Push token registration failed:', e.message);
      localStorage.removeItem('push_token_registered');
      if (retryCount < 2) {
        setTimeout(() => registerPushToken(retryCount + 1), 3000 * (retryCount + 1));
      }
    }
  };

  useEffect(() => {
    // Safety valve: force loading=false after 5s no matter what
    const safetyTimer = setTimeout(() => {
      setLoading(false);
    }, 5000);
    checkAuth().finally(() => clearTimeout(safetyTimer));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for app resume events and refresh auth if needed
  useEffect(() => {
    const handleAppResume = (event) => {
      const { pausedMs } = event.detail || {};
      if (pausedMs > 300000) {
        checkAuth();
      }
    };
    window.addEventListener('appResume', handleAppResume);

    // Web browsers: re-check auth whenever the tab becomes visible again.
    // This keeps the `user` object (and therefore `is_vip` / `is_gold`)
    // fresh when an admin updates the user's tier server-side and the user
    // returns to the tab hours/days later — they'll immediately see the
    // unlocked VIP area instead of the "Voglio accedere alle analisi VIP"
    // CTA. We throttle to at most once every 30s to avoid hammering /auth/me.
    let lastVisibilityCheckAt = 0;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('auth_token')) return; // guests: no point
      const now = Date.now();
      if (now - lastVisibilityCheckAt < 30000) return;
      lastVisibilityCheckAt = now;
      checkAuth();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('appResume', handleAppResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    console.log('[RVINCENTE] checkAuth: starting...');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const storedToken = localStorage.getItem('auth_token');
      
      if (storedToken) {
        console.log('[RVINCENTE] checkAuth: found stored token, verifying...');
        localStorage.removeItem('logged_out');
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const response = await axios.get(`${API}/auth/me`, { signal: controller.signal });
          console.log('[RVINCENTE] checkAuth: token valid, user=', response.data?.email);
          setUserAndCache(response.data);
          if (response.data?.access_token) {
            localStorage.setItem('auth_token', response.data.access_token);
          }
          if (response.data) {
            registerPushToken();
          }
          return;
        } catch (tokenErr) {
          const status = tokenErr?.response?.status;
          if (status === 401 || status === 403) {
            // Token genuinely invalid/expired — clear and continue as guest
            console.log('[RVINCENTE] checkAuth: token expired (', status, '), clearing...');
            setUserAndCache(null);
            localStorage.removeItem('auth_token');
            delete axios.defaults.headers.common['Authorization'];
          } else {
            // Network error or server down (502/503/504) — KEEP token and cached user
            console.log('[RVINCENTE] checkAuth: server error (', status || 'network', '), keeping cached user');
            const cached = localStorage.getItem('cached_user');
            if (cached) {
              try { setUserAndCache(JSON.parse(cached)); } catch { /* ignore parse error */ }
            }
          }
          return;
        }
      }
      
      // No localStorage token — check if user explicitly logged out
      const explicitlyLoggedOut = localStorage.getItem('logged_out') === 'true';
      if (explicitlyLoggedOut) {
        console.log('[RVINCENTE] checkAuth: user explicitly logged out');
        setUserAndCache(null);
        return;
      }
      
      // Not explicitly logged out — try cookie-based auth (TWA ↔ Chrome sharing)
      console.log('[RVINCENTE] checkAuth: trying cookie auth...');
      try {
        const cookieController = new AbortController();
        const cookieTimeout = setTimeout(() => cookieController.abort(), 3000);
        const response = await axios.get(`${API}/auth/me`, { 
          withCredentials: true, 
          signal: cookieController.signal 
        });
        clearTimeout(cookieTimeout);
        if (response.data && response.data.user_id) {
          console.log('[RVINCENTE] checkAuth: cookie auth success, user=', response.data?.email);
          setUserAndCache(response.data);
          localStorage.removeItem('logged_out');
          if (response.data.access_token) {
            localStorage.setItem('auth_token', response.data.access_token);
          }
          registerPushToken();
          return;
        }
      } catch {
        console.log('[RVINCENTE] checkAuth: cookie auth failed (expected for guests)');
        // Cookie also invalid or timed out — user is not authenticated
      }
      setUserAndCache(null);
    } catch (error) {
      console.error('[RVINCENTE] checkAuth: unexpected error:', error?.message);
      // Keep cached user and token on unexpected errors (deploy, network, abort)
      const cached = localStorage.getItem('cached_user');
      if (cached) {
        try { setUser(JSON.parse(cached)); } catch { /* ignore */ }
      }
    } finally {
      clearTimeout(timeout);
      console.log('[RVINCENTE] checkAuth: DONE, setting loading=false');
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    if (response.data.access_token) {
      localStorage.setItem('auth_token', response.data.access_token);
    }
    setUserAndCache(response.data.user);
    // Register push token after login
    registerPushToken();
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setUserAndCache(response.data.user);
    // Register push token after registration
    registerPushToken();
    return response.data;
  };

  const loginWithGoogle = () => {
    // GIS popup flow is handled directly in LoginPage.jsx
    // This function is kept for interface compatibility only
    console.warn('loginWithGoogle: use GIS inline flow from LoginPage');
  };

  const logout = async () => {
    // Mark as explicitly logged out BEFORE clearing storage
    localStorage.setItem('logged_out', 'true');
    
    // Clear ALL localStorage except the logout flag
    const keys = Object.keys(localStorage);
    keys.forEach(key => { if (key !== 'logged_out') localStorage.removeItem(key); });
    sessionStorage.clear();
    
    delete axios.defaults.headers.common['Authorization'];
    
    // Call backend to clear httponly cookie
    try { 
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout API error:", error);
    }
    
    // Clear all possible cookies via JS
    document.cookie.split(";").forEach(c => {
      const name = c.split("=")[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; samesite=none`;
    });
    
    setUserAndCache(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, setUser: setUserAndCache, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
